import React, { useState, useEffect } from 'react';
import { Typography, TextField, FormControl, InputLabel, Select,
  MenuItem, Button, Grid, Box, Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const HireTechTalent = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    aboutCompany: '',
    title: '',
    opportunityType: '',
    positions: '',
    location: '',
    duration: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('hireTalentData');
    if (stored) {
      setFormData((prev) => ({ ...prev, ...JSON.parse(stored) }));
    }

    const token = localStorage.getItem('company_token');
    if (token) {
      const companyNameFromToken = decodeCompanyNameFromToken(token);

      if (companyNameFromToken) {
        setFormData((prev) => ({ ...prev, companyName: companyNameFromToken }));
      }
    }
  }, []);

  const decodeCompanyNameFromToken = (token) => {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1])); 
      return decoded.companyName || '';
    } catch (error) {
      console.error('Error decoding token:', error);
      return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleNext = () => {
    const errors = {};
    if (!formData.companyName) errors.companyName = 'Company name is required';
    if (!formData.website) errors.website = 'Website is required';
    if (!formData.aboutCompany) errors.aboutCompany = 'About section is required';
    if (!formData.title) errors.title = 'Opportunity title is required';
    if (!formData.opportunityType) errors.opportunityType = 'Select an opportunity type';
    if (!formData.positions) errors.positions = 'Number of positions is required';
    if (!formData.location) errors.location = 'Please select a location';

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const stored = JSON.parse(localStorage.getItem('hireTalentData')) || {};
    localStorage.setItem('hireTalentData', JSON.stringify({ ...stored, ...formData }));
    navigate('/organizations/hire-tech-talents');
  };

  return (
    <div className="students-flex">
      <Card
        sx={{
          width : '100%',
          maxWidth : 900,
          p: 4,
          boxShadow: 6,
          borderRadius: 4,
          backgroundColor: '#f8f9fd',
        }}
      >
        <Box sx={{ maxWidth : 900, width : '100%' }}>
          <Typography
            variant="h4"
            align="center"
            fontWeight={700}
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' } }}
          >
            Hire Tech Talent
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            color="text.secondary"
            mb={4}
            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            Share your opportunity details with us
          </Typography>

          <Box component="form" noValidate autoComplete="off">
            {/* SECTION 1: COMPANY DETAILS */}
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1rem' } }}
            >
              SECTION 1: COMPANY DETAILS
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="1. Company Name"
                  name="companyName"
                  placeholder="e.g., Huebits Technologies"
                  value={formData.companyName}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  required
                  error={!!formErrors.companyName}
                  helperText={formErrors.companyName}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="2. Company Website"
                  name="website"
                  placeholder="e.g., https://www.huebits.in"
                  value={formData.website}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formErrors.website}
                  helperText={formErrors.website}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="3. About the Company"
                  name="aboutCompany"
                  placeholder="Briefly describe your company..."
                  multiline
                  minRows={3}
                  value={formData.aboutCompany}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formErrors.aboutCompany}
                  helperText={formErrors.aboutCompany}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                />
              </Grid>
            </Grid>

            {/* SECTION 2: OPPORTUNITY OVERVIEW */}
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              mt={4}
              sx={{ fontSize: { xs: '1rem', sm: '1rem' } }}
            >
              SECTION 2: OPPORTUNITY OVERVIEW
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="4. Opportunity Title"
                  name="title"
                  placeholder="e.g., Backend Developer Intern"
                  value={formData.title}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formErrors.title}
                  helperText={formErrors.title}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!formErrors.opportunityType}>
                  <InputLabel                   sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}>5. Opportunity Type</InputLabel>
                  <Select
                    name="opportunityType"
                    value={formData.opportunityType}
                    onChange={handleChange}
                    label="Opportunity Type"
                    sx={{
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    <MenuItem value="Internship">Internship</MenuItem>
                    <MenuItem value="Full-time Job">Full-time Job</MenuItem>
                    {/* <MenuItem value="Freelance">Freelance</MenuItem>
                    <MenuItem value="Contract">Contract</MenuItem> */}
                  </Select>
                  {formErrors.opportunityType && (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.8rem' }}>
                      {formErrors.opportunityType}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="6. Number of Positions"
                  name="positions"
                  type="number"
                  placeholder="e.g., 3"
                  value={formData.positions}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formErrors.positions}
                  helperText={formErrors.positions}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!formErrors.location}>
                  <InputLabel>7. Location</InputLabel>
                  <Select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    label="Location"
                    sx={{
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}
                  >
                    <MenuItem value="">Select Location</MenuItem>
                    <MenuItem value="Remote">Remote</MenuItem>
                    <MenuItem value="On-site">On-site</MenuItem>
                    <MenuItem value="Hybrid">Hybrid</MenuItem>
                  </Select>
                  {formErrors.location && (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.8rem' }}>
                      {formErrors.location}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="8. Duration"
                  name="duration"
                  placeholder="e.g., 6 months, or N/A for jobs"
                  value={formData.duration}
                  onChange={handleChange}
                  fullWidth
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                />
              </Grid>
            </Grid>
          {/* Buttons */}
          <Box mt={4} display="flex" justifyContent="space-between">
          <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate('/organizations/dashboard/pending-project')}
          sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
          Back
          </Button>
          <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
          Save & Continue
      </Button>
      </Box>
      </Box>
      </Box>
    </Card>
  </div>
  );
  };

export default HireTechTalent;
           
